import type { ReactNode } from 'react'

/**
 * Teclado numérico compartido para el código de 6 dígitos del jugador.
 * Nació en `kit/KitPinScreen.tsx` (tablet del club) y hoy lo usan las dos
 * superficies que piden el código — la tablet y `/login` desde el celular
 * del jugador —, así que vive acá y no duplicado: teclas grandes, sin
 * teclado nativo (nada de autocorrector ni sugerencias) y el foco nunca
 * salta a otro campo.
 *
 * Los estados (dígitos tipeados, busy, lockout del 429) los maneja el
 * caller: esto es sólo la superficie de input. El caller compone las tres
 * piezas en este orden y sin nada en el medio: `PinDots` → `PinStatus`
 * (slot de alto fijo para el error / "Verificando…" / el lockout) →
 * `PinKeypad`. El slot no es decorativo: es lo que evita que el teclado se
 * mueva cuando aparece un mensaje.
 */

export const PIN_LENGTH = 6

const PAD_KEYS: (number | 'delete' | null)[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'delete']
const KEY_CLASS =
  'btn h-20 select-none text-2xl font-medium hover:border-[var(--club-primary)] disabled:opacity-40'

/**
 * Indicador de progreso: un punto por dígito, relleno con el color del club.
 *
 * `shakeKey` es el acuse de un intento fallido: cada vez que cambia, los
 * puntos hacen un shake corto. Es la señal de error que NO mueve nada de su
 * lugar (transform puro, sin reflow) — el texto del error va en `PinStatus`,
 * que ocupa un alto fijo.
 */
export function PinDots({
  filled,
  length = PIN_LENGTH,
  shakeKey = 0,
  className = '',
}: {
  filled: number
  length?: number
  shakeKey?: number
  className?: string
}) {
  return (
    <div
      // El key remonta el contenedor para que la animación vuelva a correr
      // en el segundo error seguido (si no, el nodo ya la tiene consumida).
      key={shakeKey}
      className={[
        'flex justify-center gap-md',
        shakeKey > 0 ? 'pin-shake' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={`${filled} de ${length} dígitos`}
    >
      {Array.from({ length }, (_, i) => (
        <span
          key={i}
          className={[
            'h-5 w-5 rounded-full border-2',
            i < filled
              ? 'border-[var(--club-primary)] bg-[var(--club-primary)]'
              : 'border-outline',
          ].join(' ')}
        />
      ))}
    </div>
  )
}

/**
 * Slot de estado del pad — va SIEMPRE entre los puntos y las teclas, montado
 * aunque no haya nada que decir, con alto fijo (`PIN_STATUS_HEIGHT_CLASS`).
 *
 * El alto fijo es el punto: hasta el 2026-09-07 el "PIN incorrecto" entraba
 * como un banner que empujaba el teclado hacia abajo, así que el jugador que
 * ya estaba tipeando le erraba a la tecla siguiente y volvía a fallar
 * (reporte de mg). El mensaje ahora aparece y desaparece sin mover un pixel
 * del teclado; el énfasis lo pone el color y el shake de los puntos.
 *
 * `aria-live="polite"` en el contenedor (no en el mensaje) para que el
 * lector de pantalla anuncie el cambio aunque el nodo interno nazca y muera.
 */
export const PIN_STATUS_HEIGHT_CLASS = 'h-14'

export function PinStatus({
  tone = 'muted',
  className = '',
  children,
}: {
  tone?: 'muted' | 'error'
  className?: string
  children?: ReactNode
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        'flex items-center justify-center overflow-hidden px-sm text-center',
        PIN_STATUS_HEIGHT_CLASS,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children ? (
        <p
          className={[
            'm-0 text-base font-medium',
            tone === 'error' ? 'text-danger' : 'text-text-muted',
          ].join(' ')}
        >
          {children}
        </p>
      ) : null}
    </div>
  )
}

/** Grilla 3×4 con 0-9 y borrar. `onPress` recibe el dígito o `'delete'`. */
export function PinKeypad({
  onPress,
  disabled = false,
  className = '',
}: {
  onPress: (key: number | 'delete') => void
  disabled?: boolean
  className?: string
}) {
  return (
    <div className={['grid grid-cols-3 gap-md', className].join(' ')}>
      {PAD_KEYS.map((key, i) =>
        key === null ? (
          <div key={`blank-${i}`} />
        ) : (
          <button
            key={key}
            type="button"
            onClick={() => onPress(key)}
            disabled={disabled}
            aria-label={key === 'delete' ? 'Borrar dígito' : `Dígito ${key}`}
            className={KEY_CLASS}
          >
            {key === 'delete' ? '←' : key}
          </button>
        ),
      )}
    </div>
  )
}
